import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types/base-types";
import { extractFriendlyName } from "../../relation/relation-helpers";
import { asDataItem, getGroupingValue } from "./helpers";
import type { GroupedItem, NumberRangeConfig } from "./types";

export function groupByNumberRange<T extends BaseEntity>(
  data: T[],
  fieldId: string,
  _fieldConfig: SimpleFieldConfig,
  numberRangesConfig?: NumberRangeConfig
): GroupedItem<T>[] {
  const grouped = new Map<string, T[]>();

  const getRangeKey = (value: number): string => {
    if (numberRangesConfig) {
      for (const range of numberRangesConfig.ranges) {
        if (value >= range.min && (range.max === null || value <= range.max)) {
          return range.label;
        }
      }
    }
    if (value < 0) return "negative";
    if (value === 0) return "zero";
    if (value < 10) return "1-9";
    if (value < 50) return "10-49";
    if (value < 100) return "50-99";
    if (value < 500) return "100-499";
    if (value < 1000) return "500-999";
    return "1000+";
  };

  const getRangeLabel = (key: string): string => {
    if (numberRangesConfig) {
      for (const range of numberRangesConfig.ranges) {
        if (range.label === key) {
          return `${range.min} - ${range.max === null ? "∞" : range.max}`;
        }
      }
    }
    const labels: Record<string, string> = {
      negative: "Negative",
      zero: "Zero",
      "1-9": "1 - 9",
      "10-49": "10 - 49",
      "50-99": "50 - 99",
      "100-499": "100 - 499",
      "500-999": "500 - 999",
      "1000+": "1000+",
      "no-value": "(No Value)",
    };
    return labels[key] ?? key;
  };

  data.forEach((item) => {
    const raw = item[fieldId];
    const numValue = typeof raw === "number" ? raw : parseFloat(String(raw ?? ""));
    const key = isNaN(numValue) ? "no-value" : getRangeKey(numValue);
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(item);
  });

  const result: GroupedItem<T>[] = [];
  const DEFAULT_BUCKET_ORDER = [
    "negative",
    "zero",
    "1-9",
    "10-49",
    "50-99",
    "100-499",
    "500-999",
    "1000+",
  ];
  // With a custom `numberRanges` config the bucket keys are the CONFIGURED
  // labels, not the default bucket names — emitting only the default order
  // dropped every grouped row from the table (the header/rows were built but
  // never pushed), leaving the rep an empty list they believed was filtered.
  // Emit configured labels in their declared order, then any bucket that is
  // still unaccounted for, and always finish with "no-value".
  const seen = new Set<string>();
  const order: string[] = [];
  const pushKey = (key: string): void => {
    if (key === "no-value" || seen.has(key)) return;
    seen.add(key);
    order.push(key);
  };
  numberRangesConfig?.ranges.forEach((range) => pushKey(range.label));
  DEFAULT_BUCKET_ORDER.forEach(pushKey);
  Array.from(grouped.keys()).forEach(pushKey);
  order.push("no-value");

  order.forEach((key) => {
    const items = grouped.get(key);
    if (!items) return;
    const label = getRangeLabel(key);
    result.push({
      type: "group-header",
      id: `group-${fieldId}-${key}`,
      groupKey: key,
      groupLabel: label,
      groupValue: key,
      itemCount: items.length,
    });
    items.forEach((item) =>
      result.push(asDataItem(item, { type: "data", groupKey: key, groupLabel: label }))
    );
  });

  return result;
}

export function groupByTags<T extends BaseEntity>(
  data: T[],
  fieldId: string,
  _fieldConfig: SimpleFieldConfig
): GroupedItem<T>[] {
  const grouped = new Map<string, T[]>();

  data.forEach((item) => {
    const raw = item[fieldId];
    const tagArray: string[] = Array.isArray(raw) ? (raw as string[]) : raw ? [String(raw)] : [];

    if (tagArray.length === 0) {
      if (!grouped.has("no-tags")) grouped.set("no-tags", []);
      grouped.get("no-tags")!.push(item);
    } else {
      tagArray.forEach((tag) => {
        if (!grouped.has(tag)) grouped.set(tag, []);
        grouped.get(tag)!.push(item);
      });
    }
  });

  const result: GroupedItem<T>[] = [];
  Array.from(grouped.keys())
    .sort()
    .forEach((key) => {
      const items = grouped.get(key)!;
      const label = key === "no-tags" ? "No Tags" : key;
      result.push({
        type: "group-header",
        id: `group-${fieldId}-${key}`,
        groupKey: key,
        groupLabel: label,
        groupValue: key,
        itemCount: items.length,
      });
      items.forEach((item) => result.push(asDataItem(item, { type: "data", groupKey: key })));
    });

  return result;
}

export function groupByText<T extends BaseEntity>(
  data: T[],
  fieldId: string,
  _fieldConfig: SimpleFieldConfig
): GroupedItem<T>[] {
  const grouped = new Map<string, T[]>();

  data.forEach((item) => {
    const value = getGroupingValue(item, fieldId);
    let key: string;

    if (typeof value === "object" && value !== null && (value as Record<string, unknown>).id) {
      const friendlyName = extractFriendlyName(value, fieldId);
      key = !friendlyName?.trim() ? "no-value" : friendlyName.substring(0, 1).toUpperCase();
    } else if (!value || String(value).trim() === "") {
      key = "no-value";
    } else {
      key = String(value).substring(0, 1).toUpperCase();
    }

    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(item);
  });

  const result: GroupedItem<T>[] = [];
  Array.from(grouped.keys())
    .sort()
    .forEach((key) => {
      const items = grouped.get(key)!;
      let label = key === "no-value" ? "(No Value)" : key;

      if (items.length > 0 && key !== "no-value") {
        const value = getGroupingValue(items[0], fieldId);
        if (typeof value === "object" && value !== null && (value as Record<string, unknown>).id) {
          const friendlyName = extractFriendlyName(value, fieldId);
          if (friendlyName) label = `${key} - ${friendlyName}`;
        }
      }

      result.push({
        type: "group-header",
        id: `group-${fieldId}-${key}`,
        groupKey: key,
        groupLabel: label,
        groupValue: key,
        itemCount: items.length,
      });
      items.forEach((item) =>
        result.push(asDataItem(item, { type: "data", groupKey: key, groupLabel: label }))
      );
    });

  return result;
}
